/**
 * Talking to the MCP server's consent endpoints.
 *
 * Every call carries the signed-in member's own Supabase access token in an
 * `Authorization` header — explicitly, never as an ambient cookie. That is what
 * lets the MCP server refuse a `*` CORS policy: a custom header cannot be
 * attached cross-origin without a preflight, and its allowlist answers that
 * preflight for this origin only.
 *
 * The decision call deliberately does not follow a redirect. A cross-origin
 * `fetch` that follows one ends at the assistant's own callback, which has no
 * CORS headers; one that does not follow cannot read `Location`. So the server
 * answers with the destination as JSON and this page navigates to it, which is
 * also the only way the browser ends up at the callback as a real navigation
 * rather than a background request.
 */

export interface ConsentContext {
  readonly clientName: string;
  readonly tenantName: string | null;
  readonly role: "owner" | "member";
  readonly scopes: readonly string[];
  /**
   * Where approving delivers the authorization code.
   *
   * Client registration is open, so `clientName` is a string whoever registered
   * chose: "Claude Code" pointed at `https://evil.example/callback` renders
   * exactly as trustworthy as the real one. The destination is the single fact
   * on this screen a client cannot forge, because it is the address the code is
   * actually sent to. It is required, not optional — a screen that cannot say
   * where the grant goes has nothing to offer the person deciding.
   */
  readonly redirectOrigin: string;
}

/**
 * Why a consent attempt cannot proceed. Every value is a static code from the
 * server; a provider message is never surfaced, and neither is a raw error.
 */
export type ConsentFailure =
  | "sign_in_required"
  | "tenant_selection_required"
  | "consent_request_invalid"
  | "unknown_client"
  | "not_authorized"
  | "endpoint_unavailable"
  | "unreachable";

export type ConsentContextResult =
  | { readonly kind: "ok"; readonly context: ConsentContext }
  | { readonly kind: "failed"; readonly reason: ConsentFailure };

export type ConsentDecisionResult =
  | { readonly kind: "redirect"; readonly url: string }
  | { readonly kind: "failed"; readonly reason: ConsentFailure };

export interface ConsentApi {
  context(
    consentRequest: string,
    accessToken: string,
  ): Promise<ConsentContextResult>;
  decide(
    consentRequest: string,
    decision: "approve" | "deny",
    accessToken: string,
  ): Promise<ConsentDecisionResult>;
}

const FAILURES: readonly ConsentFailure[] = [
  "sign_in_required",
  "tenant_selection_required",
  "consent_request_invalid",
  "unknown_client",
  "not_authorized",
];

function failureFrom(body: unknown, status: number): ConsentFailure {
  const code =
    typeof body === "object" && body !== null && "error" in body
      ? (body as { error?: unknown }).error
      : undefined;
  if (
    typeof code === "string" &&
    (FAILURES as readonly string[]).includes(code)
  ) {
    return code as ConsentFailure;
  }
  if (status === 401) return "sign_in_required";
  if (status === 409) return "tenant_selection_required";
  if (status === 403) return "not_authorized";
  return "endpoint_unavailable";
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * `endpoint` is the published MCP address, e.g.
 * `https://api.staging.obsina.com/mcp`. The consent routes hang off it, so the
 * app never has to know the server's layout beyond the one address the
 * deployment already publishes.
 */
export function createConsentApi(
  endpoint: string,
  fetchImpl: typeof fetch = fetch,
): ConsentApi {
  const base = endpoint.replace(/\/+$/u, "");
  return {
    async context(consentRequest, accessToken) {
      const url = new URL(`${base}/authorize/context`);
      url.searchParams.set("mcp_consent", consentRequest);
      let response: Response;
      try {
        response = await fetchImpl(url.toString(), {
          method: "GET",
          headers: {
            accept: "application/json",
            authorization: `Bearer ${accessToken}`,
          },
        });
      } catch {
        return { kind: "failed", reason: "unreachable" };
      }
      const body = await readJson(response);
      if (!response.ok) {
        return { kind: "failed", reason: failureFrom(body, response.status) };
      }
      const parsed = body as {
        client_name?: unknown;
        tenant_name?: unknown;
        role?: unknown;
        scopes?: unknown;
        redirect_origin?: unknown;
      } | null;
      if (
        !parsed ||
        typeof parsed.client_name !== "string" ||
        !Array.isArray(parsed.scopes) ||
        (parsed.role !== "owner" && parsed.role !== "member") ||
        // Fail closed on a missing destination rather than rendering a screen
        // that asks for approval without saying where the grant goes. That
        // screen is the one an open-registration attacker wants.
        typeof parsed.redirect_origin !== "string" ||
        parsed.redirect_origin.length === 0
      ) {
        // A body this page cannot read is not a consent screen it may render:
        // the whole point of the screen is stating exactly what is being agreed
        // to, and a half-parsed one would state less than that.
        return { kind: "failed", reason: "endpoint_unavailable" };
      }
      return {
        kind: "ok",
        context: {
          clientName: parsed.client_name,
          tenantName:
            typeof parsed.tenant_name === "string" ? parsed.tenant_name : null,
          role: parsed.role,
          scopes: parsed.scopes.filter(
            (scope): scope is string => typeof scope === "string",
          ),
          redirectOrigin: parsed.redirect_origin,
        },
      };
    },

    async decide(consentRequest, decision, accessToken) {
      let response: Response;
      try {
        response = await fetchImpl(`${base}/authorize`, {
          method: "POST",
          headers: {
            accept: "application/json",
            "content-type": "application/x-www-form-urlencoded",
            authorization: `Bearer ${accessToken}`,
          },
          body: new URLSearchParams({
            consent_request: consentRequest,
            decision,
          }).toString(),
          // See the module note: the destination is data here, not a hop.
          redirect: "manual",
        });
      } catch {
        return { kind: "failed", reason: "unreachable" };
      }
      const body = await readJson(response);
      if (!response.ok) {
        return { kind: "failed", reason: failureFrom(body, response.status) };
      }
      const target =
        typeof body === "object" && body !== null && "redirect_to" in body
          ? (body as { redirect_to?: unknown }).redirect_to
          : undefined;
      if (typeof target !== "string" || target.length === 0) {
        return { kind: "failed", reason: "endpoint_unavailable" };
      }
      return { kind: "redirect", url: target };
    },
  };
}
